import { uploadFileApi } from "@/api/services/global/file";
import { IconAttachFile } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { ACCEPT_ERROR, COMMON_FILE_TYPES_EXTENSION, COMMON_MINE_TYPES, FILE_UPLOAD_FOR, MAX_SIZE_ERROR, MIME_TYPES } from "@/constants/file";
import { MessageFileItem } from "@/features/chat/components/MessageFileItem";
import {
  DEFAULT_INPUT_HEIGHT,
  DEFAULT_INPUT_ROWS,
  HEADER_DEFAULT_TOTAL_HEIGHT,
  LINE_HEIGHT,
  MIN_INPUT_HEIGHT,
  MIN_INPUT_ROWS,
} from "@/features/chat/constants/chat";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { background, button, divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertMbToByte, getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";
import { Box, CircularProgress, Stack, styled, Typography } from "@mui/material";
import { KeyboardEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

export type InputMsgProps = {
  contentWrapperRef?: React.RefObject<HTMLElement>;
  headerRef?: React.RefObject<HTMLElement>;
  disabled?: boolean;
};

export const InputMsg: React.FC<InputMsgProps> = ({ contentWrapperRef, headerRef, disabled = false }) => {
  const { currentRoom } = useChat();
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const rules: TFileRules = {
    acceptMimeTypes: COMMON_MINE_TYPES,
    acceptFileTypesExtension: COMMON_FILE_TYPES_EXTENSION,
  };

  const isRomDeleted = useMemo(() => !!currentRoom.roomData?.creator.deletedAt || disabled, [currentRoom, disabled]);

  const handleSendMessage = async () => {
    try {
      setLoading(true);
      const arrFilePath: string[] = [];

      if (uploadedFiles.length > 0) {
        const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);

        for (const file of uploadedFiles) {
          const isValid = file.type
            ? (rules.acceptMimeTypes || []).includes(file.type)
            : extensions.includes(file.name.split(".").pop()?.toLowerCase() ?? "");

          if (!isValid) {
            toast.error(ACCEPT_ERROR(extensions));
            return;
          }

          const rs = await uploadFileApi({
            file,
            uploadFor: FILE_UPLOAD_FOR.CHAT_FILE,
          });
          arrFilePath.push(rs.data.filePath);
        }
      }

      await currentRoom.sendMsg(inputValue, arrFilePath);

      setInputValue("");
      setUploadedFiles([]);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };
  const handleUploadFile = async (files: File[]) => {
    if (files.some((file) => file.size === 0)) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }
    if (
      !!rules.acceptMimeTypes &&
      files.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
    ) {
      const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
      toast.error(ACCEPT_ERROR(extensions));
      return;
    }

    const invalidPdf = await checkFileListWhetherIsActualPdf(files);
    if (invalidPdf) {
      toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
      return;
    }

    const maxSize = convertMbToByte(10);
    if (files.some((f) => f.size > maxSize)) {
      toast.error(MAX_SIZE_ERROR(maxSize));
      return;
    }

    if (files.length + uploadedFiles.length > 8) {
      toast.error(`ファイルは最大8件までアップロードできます。`);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      if (files.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }
      setUploadedFiles((prev) => [...prev, ...files]);
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (loading || e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      if (!inputValue) return;
      await handleSendMessage();
    }
  };

  const onFocus = async () => {
    await currentRoom?.readMsg();
  };

  const [showDropZone, setShowDropZone] = useState(false);

  useEffect(() => {
    const handleDragEnter = (event: DragEvent) => {
      event.preventDefault();
      setShowDropZone(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null && showDropZone) {
        setShowDropZone(false);
      }
    };

    const handleDragEnd = (event: DragEvent) => {
      event.preventDefault();
      setShowDropZone(false);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [showDropZone]);

  const onDrop = async (acceptedFiles: File[]) => {
    setShowDropZone(false);
    try {
      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      const maxSize = convertMbToByte(10);
      if (acceptedFiles.some((f) => f.size > maxSize)) {
        toast.error(MAX_SIZE_ERROR(maxSize));
        return;
      }

      if (acceptedFiles.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }

      setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    } catch (error) {
      showError(error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const filesContainerRef = useRef<HTMLDivElement>(null);

  const [inputMaxHeight, setInputMaxHeight] = useState<number>(DEFAULT_INPUT_HEIGHT);
  const [inputMaxRows, setInputMaxRows] = useState<number>(DEFAULT_INPUT_ROWS);

  useEffect(() => {
    function getHeaderHeight() {
      return headerRef?.current?.clientHeight ?? HEADER_DEFAULT_TOTAL_HEIGHT;
    }
    function getContentHeight() {
      return contentWrapperRef?.current?.clientHeight ?? window.innerHeight;
    }
    function getFilesContainerHeight() {
      return filesContainerRef?.current?.clientHeight ?? 0;
    }
    function updateMaxSize() {
      const contentH = getContentHeight();
      const headerH = getHeaderHeight();
      const filesH = getFilesContainerHeight();
      const minimalGap = 32;
      const filesGap = filesH > 0 ? 8 : 0;
      const maxAvailable = Math.max(MIN_INPUT_HEIGHT, contentH - headerH - minimalGap - filesH - filesGap);
      setInputMaxHeight(maxAvailable);
      const rows = Math.max(MIN_INPUT_ROWS, Math.floor(maxAvailable / LINE_HEIGHT));
      setInputMaxRows(rows);
    }
    updateMaxSize();
    window.addEventListener("resize", updateMaxSize);
    // Use ResizeObserver to watch for changes in files container height
    const resizeObserver = new ResizeObserver(() => {
      updateMaxSize();
    });
    // Use setTimeout to ensure the ref is available after render
    const timeoutId = setTimeout(() => {
      if (filesContainerRef.current) {
        resizeObserver.observe(filesContainerRef.current);
      }
    }, 0);
    return () => {
      window.removeEventListener("resize", updateMaxSize);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [contentWrapperRef, headerRef, uploadedFiles.length]);

  return (
    <>
      <InputMsgWrapper>
        <Button component="label" disabled={isRomDeleted}>
          <IconAttachFile width={24} height={24} />
          <VisuallyHiddenInput
            type="file"
            onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
            accept={rules?.acceptFileTypesExtension?.join(",")}
            onClick={(event) => {
              event.currentTarget.value = "";
            }}
            multiple={true}
          />
        </Button>
        <Box flex={1} position="relative">
          {uploadedFiles.length > 0 && (
            <Stack
              ref={filesContainerRef}
              direction="row"
              flexWrap="wrap"
              columnGap={4}
              rowGap={1}
              width="calc(100% - 32px)"
              maxWidth={620}
              mb={1}
              zIndex={1000}
              p={1}
              borderRadius={rounded.sm}
              border={`1px solid ${divider.middle}`}
              sx={{
                bgcolor: background.primary,
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
              }}
            >
              {uploadedFiles.map((file, index) => {
                if (MIME_TYPES.IMAGE.some((item) => file.type && item.includes(file.type))) {
                  return (
                    <Avatar
                      key={index}
                      imageSrc={URL.createObjectURL(file)}
                      width={120}
                      height={120}
                      onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))}
                    />
                  );
                }
                return (
                  <MessageFileItem key={index} file={file} onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))} />
                );
              })}
            </Stack>
          )}
          <Input
            sx={{
              boxShadow: "0px 4px 8px 0px #0000001A",
              borderRadius: rounded.xs,
              maxHeight: inputMaxHeight,
              overflow: "auto",
              resize: "none",
            }}
            multiline
            inputRef={inputRef}
            maxRows={inputMaxRows}
            autoComplete="off"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            fullWidth
            size="lg"
            disabled={loading || isRomDeleted}
          />
        </Box>

        <Button onClick={handleSendMessage} disabled={loading || isRomDeleted || (!inputValue && uploadedFiles.length === 0)}>
          {loading ? <CircularProgress size={20} color="inherit" /> : <Typography variant="sub16Semi">＞</Typography>}
        </Button>
      </InputMsgWrapper>

      {showDropZone && !isRomDeleted && (
        <Box
          {...getRootProps()}
          position="absolute"
          zIndex={5000}
          width="772px"
          height="calc(100% - 340px)"
          bottom={100}
          sx={{
            border: `1px dashed ${divider.middle}`,
            borderColor: isDragActive ? button.primary : undefined,
            backgroundColor: background.white,
            opacity: 0.9,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body14">こちらにドラッグ＆ドロップしてください</Typography>
        </Box>
      )}
    </>
  );
};

const InputMsgWrapper = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  background-color: ${background.primary};
  .MuiInputBase-root {
    padding: 0;
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
